import { theme } from '@Guidelines/Theme/theme';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Tag } from '@verclam/ui-kit';
import { formatLongCalendarDate } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/SectionTabs/SectionPanel/DocumentEntry/FieldCard/Helpers/dateFormatter';

const valueTextStyle = css`
	font-family: ${theme.fonts.display};
	font-size: 1.0625rem;
	font-weight: ${theme.fontWeights.medium};
	letter-spacing: -0.01em;
	color: ${theme.colors.ink};
	white-space: pre-wrap;
`;

const emptyStyle = css`
	${valueTextStyle}
	color: ${theme.colors.inkFaint};
`;

const tagsRowStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.375rem;
`;

const numberFormatter = new Intl.NumberFormat('fr-FR');

type Props = {
	readonly field: DocumentFieldView;
};

/**
 * Pure value formatter, read-only — no click affordance, no box of its own: `FieldCard` wraps it in
 * the dashed editable box, `SourcePanelSidebar` in its own muted block. `image` never reaches this screen
 * (excluded server-side, along with the image type itself), so no `'none'` branch is needed here.
 */
const FieldValueDisplay: React.FC<Props> = ({ field }) => {
	if (field.value === null) {
		return <span css={emptyStyle}>Non renseigné</span>;
	}

	if (typeof field.value === 'boolean') {
		return <span css={valueTextStyle}>{field.value ? 'Oui' : 'Non'}</span>;
	}

	if (typeof field.value === 'number') {
		return <span css={valueTextStyle}>{numberFormatter.format(field.value)}</span>;
	}

	if (field.fieldType.value === 'date' && typeof field.value === 'string') {
		return <span css={valueTextStyle}>{formatLongCalendarDate(field.value)}</span>;
	}

	if (Array.isArray(field.value)) {
		return (
			<div css={tagsRowStyle}>
				{field.value.map((option) => (
					<Tag key={option.value} value={option.label} />
				))}
			</div>
		);
	}

	if (field.value instanceof LabeledValueView) {
		return <span css={valueTextStyle}>{field.value.label}</span>;
	}

	if (typeof field.value === 'string') {
		return <span css={valueTextStyle}>{field.value}</span>;
	}

	// Unreachable: the `Array.isArray` branch above already excludes `readonly LabeledValueView[]`
	// at runtime, but TypeScript does not narrow a `readonly T[]` out of a union on its negative
	// branch (a known limitation, also worked around in `DocumentFieldView`'s own `resolveValue`).
	return null;
};

export default FieldValueDisplay;
